import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { subscribe, eventBus } from 'mobx-event-bus2';

import RootStore from 'stores/Root/Root';
import Inbox from 'stores/Inbox';
import KnockInbox from 'stores/KnockInbox';
import { NotificationTarget } from 'core/constants';
import { NotificationType } from 'utils/api/modules/user/user.types';
import Logger from 'utils/logger';
import { EventType } from 'utils/events/constants';

import {
  NotificationCategory,
  getCategoryForNotification,
  getTargetsForCategory,
} from 'containers/KnockNotifications/constants';

export type NotificationSource = 'legacy' | 'knock';

export interface MergedNotification extends NotificationType {
  source: NotificationSource;
}

// Unified inbox composing the legacy Inbox and Knock-backed KnockInbox. Mode toggles on
// KnockInbox.knockEnabled — legacy-only when off, merged-by-created when on.
export default class DualInbox {
  store: RootStore;

  private legacyInbox: Inbox;

  private knockInbox: KnockInbox;

  @observable initialSync?: boolean = undefined;

  @observable isLoading = false;

  @observable activeCategory: NotificationCategory = NotificationCategory.ALL;

  @observable showUnreadOnly = false;

  @observable searchQuery = '';

  constructor(rootStore: RootStore, legacyInbox: Inbox, knockInbox: KnockInbox) {
    makeObservable(this);
    this.store = rootStore;
    this.legacyInbox = legacyInbox;
    this.knockInbox = knockInbox;

    eventBus.register(this);
  }

  @computed
  get knockEnabled(): boolean {
    return this.knockInbox.knockEnabled === true;
  }

  @action.bound
  async loadInitialData(): Promise<void> {
    if (this.initialSync !== undefined) {
      return;
    }

    this.initialSync = false;
    this.isLoading = true;

    try {
      // Fetch legacy unconditionally so its notifications array is populated regardless of Knock state.
      await this.legacyInbox.loadInitialData();
      await this.knockInbox.loadInitialData();
    } catch (e) {
      Logger.error('Failed to initialize DualInbox', e);
    } finally {
      runInAction(() => {
        this.initialSync = true;
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async loadNotifications(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    try {
      const fetches: Promise<void>[] = [];
      const legacyTargets = this.legacyTargetsForActiveCategory();

      if (this.legacyInbox.hasMore) {
        fetches.push(this.legacyInbox.loadNotifications(legacyTargets));
      }
      if (this.knockEnabled && this.knockInbox.hasMore) {
        fetches.push(this.knockInbox.loadNotifications());
      }

      await Promise.all(fetches);
    } catch (e) {
      Logger.error('Failed to load more DualInbox notifications', e);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async markNotificationAsRead(uuid: string): Promise<void> {
    if (this.findSource(uuid) === 'knock') {
      await this.knockInbox.markNotificationAsRead(uuid);
      return;
    }
    await this.legacyInbox.markNotificationAsRead(uuid);
  }

  @action.bound
  async dismissNotifications(): Promise<void> {
    const tasks: Promise<void>[] = [this.legacyInbox.dismissNotifications()];
    if (this.knockEnabled) {
      tasks.push(this.knockInbox.dismissNotifications());
    }
    await Promise.all(tasks);
  }

  @action.bound
  setActiveCategory(category: NotificationCategory): void {
    if (this.activeCategory === category) return;
    this.activeCategory = category;
    this.searchQuery = '';
    // Reset legacy paging — `offset` is meaningful only against a stable target= query, and the
    // category switch changes that query. Knock buffers everything client-side, so no reset there.
    this.legacyInbox.reset();
    void this.legacyInbox.loadInitialData(this.legacyTargetsForActiveCategory());
  }

  private legacyTargetsForActiveCategory(): NotificationTarget[] | undefined {
    return this.activeCategory === NotificationCategory.ALL ? undefined : getTargetsForCategory(this.activeCategory);
  }

  @action.bound
  setShowUnreadOnly(value: boolean): void {
    this.showUnreadOnly = value;
  }

  @action.bound
  setSearchQuery(query: string): void {
    this.searchQuery = query;
  }

  @computed
  get filteredNotifications(): MergedNotification[] | undefined {
    const legacyItems = this.legacyInbox.notifications;
    const knockItems = this.knockEnabled ? this.knockInbox.notifications : undefined;

    if (legacyItems === undefined && knockItems === undefined) {
      return undefined;
    }

    const tagged: MergedNotification[] = [];
    if (legacyItems) {
      for (const n of legacyItems) tagged.push({ ...n, source: 'legacy' });
    }
    if (knockItems) {
      for (const n of knockItems) tagged.push({ ...n, source: 'knock' });
    }

    // Defensive dedup — sources are time-disjoint so collisions shouldn't happen.
    const seen = new Set<string>();
    const deduped = tagged.filter((n) => {
      if (seen.has(n.uuid)) return false;
      seen.add(n.uuid);
      return true;
    });

    deduped.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

    // Drop items whose target has no category mapping (logged via getCategoryForNotification).
    let result = deduped.filter((n) => getCategoryForNotification(n.target) !== undefined);

    if (this.activeCategory !== NotificationCategory.ALL) {
      result = result.filter((n) => getCategoryForNotification(n.target) === this.activeCategory);
    }

    if (this.showUnreadOnly) {
      result = result.filter((n) => !n.dismissed);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      // Optional chaining throughout — public submissions have no creator, so legacy items arrive with creator: {}.
      result = result.filter(
        (n) =>
          (n.creator?.name ?? '').toLowerCase().includes(query) ||
          (n.instance?.name ?? '').toLowerCase().includes(query) ||
          (n.instance?.subject ?? '').toLowerCase().includes(query)
      );
    }

    return result;
  }

  @computed
  get unreadFilteredCount(): number {
    if (this.activeCategory === NotificationCategory.ALL && !this.searchQuery && this.knockEnabled) {
      return this.knockInbox.notDismissedNotificationsCount;
    }
    return this.filteredNotifications?.filter((n) => !n.dismissed).length ?? 0;
  }

  @computed
  get notDismissedNotificationsCount(): number {
    const legacy = this.legacyInbox.notDismissedNotificationsCount;
    const knock = this.knockEnabled ? this.knockInbox.notDismissedNotificationsCount : 0;
    return legacy + knock;
  }

  @computed
  get hasMore(): boolean {
    const knockMore = this.knockEnabled && this.knockInbox.hasMore;
    return this.legacyInbox.hasMore || knockMore;
  }

  @computed
  get isDismissing(): boolean {
    return this.legacyInbox.isDismissing || (this.knockEnabled && this.knockInbox.isDismissing);
  }

  private findSource(uuid: string): NotificationSource {
    if (this.knockEnabled && this.knockInbox.notifications?.some((n) => n.uuid === uuid)) {
      return 'knock';
    }
    if (this.knockEnabled && !this.legacyInbox.notifications?.some((n) => n.uuid === uuid)) {
      Logger.warn(`DualInbox.findSource: uuid "${uuid}" not in either buffer; defaulting to legacy`);
    }
    return 'legacy';
  }

  @subscribe(EventType.LoggedOut)
  @action
  logout(): void {
    this.initialSync = undefined;
    this.isLoading = false;
    this.activeCategory = NotificationCategory.ALL;
    this.showUnreadOnly = false;
    this.searchQuery = '';
  }
}
